/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { PinTypesDaoService } from '../../dao/pin-types-dao.service';
import { PinTypesService } from '../pin-types.service';
import { PinType } from 'src/app/_model/pin-type';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { TranslateService } from '@ngx-translate/core';
import { ExtFilesService } from 'src/app/_services/ext-files/logic/ext-files.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Pins
 */
@Injectable()
export class PinTypesLogicService implements PinTypesService {
  public pinTypes$ = new BehaviorSubject<PinType[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private remoteActionService: RemoteActionService,
    private pinTypesDaoService: PinTypesDaoService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private extFilesService: ExtFilesService,
    private errorService: ErrorService
  ) {
    this.initialize();
  }

  // Public Functions

  // Resetable
  public async reset() {
    this.pinTypesDaoService.reset();
  }

  public async clear() {
    await this.pinTypesDaoService.clear();
    this.pinTypes$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemotePinTypes(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalPinTypes();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalPinTypes() {
    try {
      this.logger.log(this.constructor.name, '- Load local PinTypes');
      let pinTypes: PinType[] = await this.pinTypesDaoService.getPinTypes();

      this.pinTypes$.next(pinTypes);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local PinTypes: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemotePinTypes(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new PinTypes from IMS');

      try {
        let pinTypes: PinType[] = await this.loadRemotePinTypeBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all pinTypes (' + pinTypes.length + ' Total)'
        );
        this.pinTypes$.next(pinTypes);

        this.logger.log(this.constructor.name, 'Saving pinTypes', pinTypes);
        await this.pinTypesDaoService.savePinTypes(pinTypes);
        this.logger.log(this.constructor.name, 'Saved all PinTypes');
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading PinTypes: ',
          error?.message
        );
        throw error;
      }
    }
  }

  private async loadRemotePinTypeBatch(
    syncKey: string,
    pinTypes: PinType[],
    nextPart?: String,
  ): Promise<PinType[]> {
    let pinTypesData;
    if (nextPart) {
      pinTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        })
      );
    } else {
      pinTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncpintypes',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey
          },
        })
      );
    }

    let nextId: string;
    let nextPinTypes: PinType[] = [];
    for (const pinTypeData of pinTypesData) {
      if (pinTypeData['#nextid']) {
        nextId = pinTypeData['#nextid'];
      } else {
        nextPinTypes.push(PinType.fromDto(pinTypeData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch PinTypes: ',
      nextPinTypes.length,
      'Total'
    );

    await this.pinTypesDaoService.savePinTypes(nextPinTypes);

    let returnPinTypes = pinTypes.concat(nextPinTypes);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more pinTypes with nextid',
        nextId
      );
      return await this.loadRemotePinTypeBatch(syncKey, returnPinTypes, nextId);
    }

    return returnPinTypes;
  }

  async initializeCompleted(): Promise<void> {}
}
